/**
 * Customer Rules Engine — public serverFns.
 *
 * Thin file: every export is a `createServerFn`. Heavy lifting lives in
 * `src/lib/rules/*` so this file stays client-importable through TanStack's
 * server-fn split.
 *
 * ARCHITECTURE NOTE (Blueprint Foundation Sprint):
 * The Rules Engine is FROZEN as an internal compile target. It is not a
 * customer-facing generic workflow builder. Do not add new generic action
 * primitives, scripting, or condition operators here without an explicit
 * Pattern-Library justification. New operational behavior belongs behind a
 * policy interface in `src/lib/policies/`.
 * See `docs/architecture/policies/rules-engine-freeze.md`.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { recordAudit } from "./audit";
import { buildOrderContext } from "./rules/context";
import { runRulesFor } from "./rules/executor";
import type { Action, Condition, RuleRow, TriggerEvent } from "./rules/types";

/* ----------------------------- schemas ----------------------------- */

const LeafSchema: z.ZodType<any> = z.object({
  type: z.literal("leaf"),
  field: z.string().min(1).max(120),
  op: z.enum([
    "eq","neq","gt","gte","lt","lte","in","not_in",
    "contains","not_contains","exists","not_exists","is_true","is_false",
  ]),
  value: z.unknown().optional(),
});
const GroupSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    type: z.enum(["and","or","not"]),
    children: z.array(z.union([LeafSchema, GroupSchema])).max(50),
  }),
);
const ConditionSchema = z.union([LeafSchema, GroupSchema]);

const ActionSchema = z.object({
  kind: z.string().min(1).max(60),
  params: z.record(z.string(), z.unknown()).default({}),
});

const TriggerEnum = z.enum([
  "order.imported","order.allocated","pick.started","pack.started",
  "label.generated","shipment.created","return.received",
]);
const ScopeEnum = z.enum(["global","tenant","facility","customer"]);

const UpsertRuleSchema = z.object({
  id: z.string().uuid().optional(),
  tenant_id: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullable().optional(),
  is_active: z.boolean().default(true),
  priority: z.number().int().min(0).max(10000).default(100),
  scope: ScopeEnum.default("tenant"),
  facility_id: z.string().uuid().nullable().optional(),
  customer_id: z.string().uuid().nullable().optional(),
  trigger_event: TriggerEnum,
  condition_tree: ConditionSchema,
  actions: z.array(ActionSchema).max(50),
  effective_from: z.string().datetime().nullable().optional(),
  effective_to: z.string().datetime().nullable().optional(),
  test_mode: z.boolean().default(false),
});

/* ----------------------------- queries ----------------------------- */

export const listRules = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      tenantId: z.string().uuid(),
      trigger: TriggerEnum.optional(),
      activeOnly: z.boolean().optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    let q = (context.supabase as any)
      .from("automation_rules").select("*")
      .eq("tenant_id", data.tenantId)
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });
    if (data.trigger) q = q.eq("trigger_event", data.trigger);
    if (data.activeOnly) q = q.eq("is_active", true);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { rules: (rows ?? []) as RuleRow[] };
  });

export const getRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await (context.supabase as any)
      .from("automation_rules").select("*").eq("id", data.id).maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("rule not found");
    return { rule: row as RuleRow };
  });

export const upsertRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => UpsertRuleSchema.parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const userId = context.userId as string;

    // Phase 3 dual-write: legacy row + mirror into public.policies in one txn.
    const { id, ...rest } = data;
    const { data: rpcRow, error: rpcErr } = await supabase.rpc(
      "automation_rule_upsert_v1",
      { _id: id ?? null, _payload: rest },
    );
    if (rpcErr) throw new Error(rpcErr.message);
    const saved = rpcRow as RuleRow;

    await supabase.from("automation_rule_versions").insert({
      rule_id: saved.id,
      tenant_id: saved.tenant_id,
      version: saved.version,
      snapshot: saved,
      created_by: userId,
    });
    await recordAudit({
      supabase, tenantId: saved.tenant_id, actorId: userId,
      entityType: "automation_rule", entityId: saved.id,
      action: data.id ? "rule.update" : "rule.create",
      diff: { name: saved.name, trigger: saved.trigger_event, version: saved.version },
    });
    return { rule: saved };
  });

export const toggleRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid(), is_active: z.boolean() }).parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { data: row, error } = await supabase.rpc("automation_rule_toggle_v1", {
      _id: data.id, _is_active: data.is_active,
    });
    if (error) throw new Error(error.message);
    await recordAudit({
      supabase, tenantId: row.tenant_id, actorId: context.userId as string,
      entityType: "automation_rule", entityId: row.id,
      action: data.is_active ? "rule.enable" : "rule.disable",
    });
    return { rule: row as RuleRow };
  });

export const duplicateRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { data: row, error } = await supabase.rpc("automation_rule_duplicate_v1", {
      _id: data.id,
    });
    if (error) throw new Error(error.message);
    return { rule: row as RuleRow };
  });

export const deleteRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { data: row, error } = await supabase.rpc("automation_rule_delete_v1", {
      _id: data.id,
    });
    if (error) throw new Error(error.message);
    if (row) {
      await recordAudit({
        supabase, tenantId: row.tenant_id, actorId: context.userId as string,
        entityType: "automation_rule", entityId: data.id, action: "rule.delete",
        diff: { name: row.name },
      });
    }
    return { ok: true };
  });

/** Dry-run a rule (saved or draft) against recent orders. */
export const dryRunRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      tenantId: z.string().uuid(),
      ruleId: z.string().uuid().optional(),
      draft: UpsertRuleSchema.partial({ id: true, tenant_id: true }).optional(),
      orderIds: z.array(z.string().uuid()).max(50).optional(),
      sampleSize: z.number().int().min(1).max(50).default(10),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;

    // Determine trigger
    let trigger: TriggerEvent;
    let draftRow: any = null;
    if (data.ruleId) {
      const { data: r } = await supabase
        .from("automation_rules").select("*").eq("id", data.ruleId).maybeSingle();
      if (!r) throw new Error("rule not found");
      trigger = r.trigger_event;
    } else if (data.draft) {
      trigger = data.draft.trigger_event as TriggerEvent;
      draftRow = data.draft;
    } else {
      throw new Error("ruleId or draft required");
    }

    // Pick order sample
    let ids = data.orderIds ?? [];
    if (ids.length === 0) {
      const { data: rows } = await supabase
        .from("orders").select("id")
        .eq("tenant_id", data.tenantId)
        .order("created_at", { ascending: false })
        .limit(data.sampleSize);
      ids = (rows ?? []).map((r: any) => r.id);
    }

    const previews = [];
    for (const orderId of ids) {
      const ctx = await buildOrderContext(supabase, trigger, orderId);
      if (!ctx) continue;
      const summary = await runRulesFor(supabase, trigger, ctx, {
        dryRun: true,
        ruleId: data.ruleId,
        draft: draftRow ? {
          ...draftRow,
          condition_tree: draftRow.condition_tree as Condition,
          actions: draftRow.actions as Action[],
        } : undefined,
      });
      previews.push({ orderId, summary });
    }
    return { previews };
  });

/** List execution history with optional filters. */
export const listRuleRuns = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      tenantId: z.string().uuid(),
      ruleId: z.string().uuid().optional(),
      entityId: z.string().uuid().optional(),
      limit: z.number().int().min(1).max(500).default(100),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    let q = (context.supabase as any)
      .from("automation_rule_runs")
      .select("*, automation_rules(name,trigger_event,scope)")
      .eq("tenant_id", data.tenantId)
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.ruleId) q = q.eq("rule_id", data.ruleId);
    if (data.entityId) q = q.eq("entity_id", data.entityId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { runs: rows ?? [] };
  });

/** Manually fire rules for an existing order (admin tool / replay). */
export const runRulesForOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      orderId: z.string().uuid(),
      trigger: TriggerEnum,
      dryRun: z.boolean().default(false),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const ctx = await buildOrderContext(supabase, data.trigger, data.orderId);
    if (!ctx) throw new Error("order not found");
    const summary = await runRulesFor(supabase, data.trigger, ctx, { dryRun: data.dryRun });
    return { summary };
  });